import { toast } from 'sonner';

/**
 * „Senden rückgängig machen“: Antworten werden erst nach einer kurzen Frist
 * abgeschickt. In dieser Zeit zeigt ein Toast einen Countdown mit
 * „Rückgängig“ — ein Klick bricht den Versand ab, der Text bleibt im Editor.
 *
 * Die Frist läuft auf Modulebene, nicht im Ticket-Bildschirm: wer währenddessen
 * das Ticket wechselt, schickt die Nachricht trotzdem ab (wie bei Gmail).
 * Einstellung pro Browser (Einstellungen → Senden).
 */

const STORAGE_KEY = 'vyndesk.undoSend';
export const UNDO_SEND_CHOICES = [5, 10, 20, 30] as const;

export interface UndoSendSettings {
  enabled: boolean;
  seconds: number;
}

export function getUndoSendSettings(): UndoSendSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (raw && typeof raw.enabled === 'boolean') {
      const seconds = Number(raw.seconds);
      return { enabled: raw.enabled, seconds: UNDO_SEND_CHOICES.includes(seconds as any) ? seconds : 10 };
    }
  } catch { /* ignore */ }
  return { enabled: false, seconds: 10 };
}

export function setUndoSendSettings(settings: UndoSendSettings): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* ignore */ }
}

/** Fehler, mit dem ein abgebrochener Versand endet — Aufrufer zeigen dafür KEINE Fehlermeldung. */
export class SendUndoneError extends Error {
  code = 'SEND_UNDONE' as const;
  constructor() { super('Versand rückgängig gemacht'); }
}

export function isSendUndone(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && (error as any).code === 'SEND_UNDONE');
}

let seq = 0;

/**
 * Wartet die eingestellte Frist ab (sofort zurück, wenn ausgeschaltet).
 * Wirft SendUndoneError, wenn der Nutzer „Rückgängig“ klickt.
 */
export function waitForUndoWindow(label = 'Antwort'): Promise<void> {
  const { enabled, seconds } = getUndoSendSettings();
  if (!enabled || seconds <= 0) return Promise.resolve();

  const id = `undo-send-${++seq}`;
  return new Promise<void>((resolve, reject) => {
    let remaining = seconds;
    let done = false;

    const finish = (undone: boolean) => {
      if (done) return;
      done = true;
      clearInterval(timer);
      toast.dismiss(id);
      if (undone) {
        toast.info(`${label} wurde nicht gesendet – der Text bleibt im Editor.`);
        reject(new SendUndoneError());
      } else {
        resolve();
      }
    };

    const show = () => toast(`${label} wird in ${remaining} s gesendet …`, {
      id,
      duration: Infinity,
      action: { label: 'Rückgängig', onClick: () => finish(true) },
    });

    show();
    const timer = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) finish(false);
      else show();
    }, 1000);
  });
}
